import { zodResolver } from "@hookform/resolvers/zod";
import { CircleAlert, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { type Path, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useChangePinParticipantsPinChangePost } from "@/api-v3";
import { CardContainer } from "@/components/card-container";
import { ForgetPinButton } from "@/components/forget-pin-button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import StorageManager from "@/features/storage";
import { useToast } from "@/hooks/use-toast";
import {
  getApiValidationErrors,
  isApiValidationError,
} from "@/lib/api-validation-errors";
import { parseError } from "@/lib/error-handler";

import { type ChangePinPageProps } from "./change-pin-page.types";
import {
  ChangePinForm,
  type ChangePinFormSchema,
} from "./components/change-pin-form";
import {
  CHANGE_PIN_FIELD_NAMES,
  getChangePinFormSchema,
} from "./components/change-pin-form/change-pin-form-schema";

export const ChangePINPage = (props: ChangePinPageProps) => {
  const { isDialogMode = false, onSuccess } = props;
  const { t } = useTranslation();
  const { toast } = useToast();
  const [serverError, setServerError] = useState<{
    title: string;
    details: string;
  } | null>(null);

  const form = useForm({
    mode: "onTouched",
    resolver: zodResolver(getChangePinFormSchema(t)),
    defaultValues: {
      currentPin: "",
      newPin: "",
      newPinConfirmation: "",
    },
  });

  const { mutateAsync, isPending } = useChangePinParticipantsPinChangePost({
    request: {
      // An incorrect current PIN returns an auth-style error, but the user is still authenticated.
      // Keep them logged in so they can correct the PIN and retry.
      skipAuthErrorLogout: true,
      skipGlobalErrorToast: true,
    },
    mutation: {
      onError: (error) => {
        if (isApiValidationError(error)) {
          const { fieldErrors, formErrors } = getApiValidationErrors(error, {
            knownFields: CHANGE_PIN_FIELD_NAMES,
          });

          for (const { field, message, type } of fieldErrors) {
            form.setError(field as Path<ChangePinFormSchema>, {
              type,
              message,
            });
          }

          setServerError(
            formErrors.length > 0
              ? {
                  title: t("error"),
                  details: formErrors.map((e) => e.message).join("\n"),
                }
              : null
          );
          return;
        }
        const { title, message } = parseError(error);
        setServerError({ title: t(title), details: t(message) });
      },
      onSuccess: () => {
        StorageManager.updateRequiresPinChange(false);
        if (isDialogMode && onSuccess) onSuccess();
        toast({
          title: t("changePin_pinChangedConfirmationMessage_p1"),
          description: t("changePin_pinChangedConfirmationMessage_p2"),
        });
      },
    },
  });

  const onSubmit = async (data: ChangePinFormSchema) => {
    setServerError(null);
    const { currentPin, newPin } = data;
    await mutateAsync({
      data: {
        current_pin: currentPin,
        new_pin: newPin,
      },
    });
  };

  return (
    <CardContainer showBorder={!isDialogMode}>
      <CardContainer.Header>
        <CardContainer.HeaderContent>
          <CardContainer.CardTitle>
            {t("settings_changePin")}
          </CardContainer.CardTitle>
          <CardContainer.CardDescription>
            {t("change_pin_subtitle")}
          </CardContainer.CardDescription>
        </CardContainer.HeaderContent>
      </CardContainer.Header>
      <CardContainer.Content>
        {isDialogMode && (
          <Alert className="mb-4" variant="warning">
            <TriangleAlert className="text-yellow-900" />
            <AlertDescription>{t("changePin_msg")}</AlertDescription>
          </Alert>
        )}

        {serverError && (
          <Alert className="mb-4" variant="destructive">
            <CircleAlert />
            <AlertTitle>{serverError.title}</AlertTitle>
            <AlertDescription className="whitespace-pre-line">
              {serverError.details}
            </AlertDescription>
          </Alert>
        )}

        <ChangePinForm
          form={form}
          onSubmit={onSubmit}
          isSubmitting={isPending}
        />
        <div className="mt-4">{!isDialogMode && <ForgetPinButton />}</div>
      </CardContainer.Content>
    </CardContainer>
  );
};
